import { zodResolver } from '@hookform/resolvers/zod';
import type { UpdateCustomerResponse } from '@weft/api-definition';
import { INTEGRATION_PROVIDERS, NewCustomerSchema, UPDATE_CUSTOMER_SECTIONS_TO_SUBMIT, UpdateCustomerSchema } from '@weft/types';
import type { UpdateCustomer, UpdateCustomerSectionToSubmit } from '@weft/types';
import { fromUndefinedToNull, isNotNullOrUndefined, validateNotNullOrUndefined } from '@weft/utils';
import { useNetSuiteCustomers } from 'apps/app/src/hooks/requests/use-net-suite';
import { useUpdateCustomer } from 'apps/app/src/hooks/requests/use-update-customer';
import { useActivatedIntegration } from 'apps/app/src/hooks/use-activated-integration';
import { useValidatedParams } from 'apps/app/src/hooks/use-validated-params';
import { runMutation } from 'apps/app/src/utils/vy-mutation';
import React from 'react';
import { Helmet } from 'react-helmet';
import { FormProvider, useForm } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import { withSuspenseBoundary } from '../../../components/utility/SuspenseBoundary';
import { useCustomer } from '../../../hooks/requests/use-customer';
import { CustomerInfoViewEditChildren } from './customer-info-view-edit-components/customer-info-view-edit-children/CustomerInfoViewEditChildren';
import { CustomerInfoViewEditAddress } from './customer-info-view-edit-components/CustomerInfoViewEditAddress';
import { CustomerInfoViewEditContacts } from './customer-info-view-edit-components/CustomerInfoViewEditContacts';
import { CustomerInfoViewEditName } from './customer-info-view-edit-components/CustomerInfoViewEditName';
import { CustomerInfoViewEditSettings } from './customer-info-view-edit-components/CustomerInfoViewEditSettings';
import { buildSubmittedSectionData } from './customer-info-view-edit-mappers/build-submitted-section-data';

const CustomerInfoViewEditPage: React.FC = () => {
  const { customerId } = useValidatedParams<['customerId']>('customerId');
  const updateCustomerMutation = useUpdateCustomer(customerId);
  const queryClient = useQueryClient();
  useNetSuiteCustomers(undefined, { suspense: true });
 
  const customerResponse = useCustomer(customerId, { suspense: true });
  validateNotNullOrUndefined(customerResponse.data, 'Customer data is required');
  const { customer, children } = customerResponse.data;

  const isNetSuiteEnabled = useActivatedIntegration(INTEGRATION_PROVIDERS.netSuite, { suspense: true });

  const { ...methods } = useForm<UpdateCustomer>({
    resolver: zodResolver(NewCustomerSchema),
    defaultValues: { ...customer, sectionToSubmit: UPDATE_CUSTOMER_SECTIONS_TO_SUBMIT.none },
  });

  const handleMutationSuccess = async (data: UpdateCustomerResponse) => {
    const erpMetadata = isNotNullOrUndefined(data?.customer?.erpMetadata) ? data.customer.erpMetadata : undefined;
    data?.customer && methods.reset({ ...data.customer, sectionToSubmit: UPDATE_CUSTOMER_SECTIONS_TO_SUBMIT.none, erpMetadata });
    await queryClient.invalidateQueries('customersOverviews');
  };

  methods.watch(async () => {
    const updateCustomerData = methods.getValues();

    if (updateCustomerData.sectionToSubmit !== UPDATE_CUSTOMER_SECTIONS_TO_SUBMIT.none) {
      const sectionToSubmitDataWithCleanedSectionEnum = buildSubmittedSectionData(updateCustomerData);
      const customerInput = getCustomerInput(sectionToSubmitDataWithCleanedSectionEnum, updateCustomerData.sectionToSubmit);
        
      if (isNotNullOrUndefined(customerInput)) {
        methods.setValue('sectionToSubmit', UPDATE_CUSTOMER_SECTIONS_TO_SUBMIT.none);

        await runMutation(
          updateCustomerMutation,
          customerInput,
          {
            pending: 'Saving customer...',
            success: 'Customer saved',
            error: 'Failed to save customer',
          },
          { onSuccess: handleMutationSuccess });
      }
    }
  });

  return (
    <>
      <Helmet>
        <title>Customer Settings</title>
      </Helmet>

      <FormProvider
        {...methods}
      >

        <div className='relative flex h-fit min-h-fit flex-col gap-5'>
          <CustomerInfoViewEditName isNetSuiteEnabled={isNetSuiteEnabled}/>

          <CustomerInfoViewEditAddress/>

          <CustomerInfoViewEditSettings/>

          <CustomerInfoViewEditContacts/>

          <CustomerInfoViewEditChildren
            customerId={customerId}
            customerName={customer.name}
            customerChildren={children}
          />
        </div>
      </FormProvider>
    </>
  );
};

const CustomerInfoViewEditPageWithSuspenseBoundary = withSuspenseBoundary(CustomerInfoViewEditPage);

export { CustomerInfoViewEditPageWithSuspenseBoundary as CustomerInfoViewEditPage };

function getCustomerInput(customer: UpdateCustomer | null, sectionToSubmit: UpdateCustomerSectionToSubmit) {
  const customerInputParseResult = UpdateCustomerSchema.safeParse(customer);

  return customerInputParseResult.success ? buildCustomerInputWithNullValues(customerInputParseResult.data, sectionToSubmit) : undefined;
}

function buildCustomerInputWithNullValues(customer: UpdateCustomer, sectionToSubmit: UpdateCustomerSectionToSubmit) {  
  return sectionToSubmit === UPDATE_CUSTOMER_SECTIONS_TO_SUBMIT.details 
    ? {
      ...customer,
      externalId: fromUndefinedToNull(customer.externalId),
      taxId: fromUndefinedToNull(customer.taxId),
      crmId: fromUndefinedToNull(customer.crmId),
      erpMetadata: fromUndefinedToNull(customer.erpMetadata),
    }
    : customer;
}